<template>
  <div class="exit-survey" v-if="isVisible" @click.self="closePopup">
    <div class="exit-survey-content" :class="{
      'expanded': selectedOption === 'Other (please specify)',
      'thank-you-mode': submitted
    }">
      <div class="exit-survey-close-btn">
        <img 
          :src="require('@/assets/images/icons/ic_close_survey.svg')" 
          alt="close" 
          @click="closePopup"
        />
      </div>
      <div class="exit-survey-header">
        <p class="fs-24 font-weight-bold">Quick question before you go!</p>
      </div>
      <div class="exit-survey-body font-weight-thin fs-18" v-if="!submitted">
        <p class="text-left">What's the biggest reason you wouldn't submit your car details today?</p>
        <div class="exit-survey-options">
          <div v-for="(option, index) in surveyOptions" :key="index" class="option">
            <input 
              class="cursor-pointer"
              type="radio" 
              :id="`option-${index}`" 
              :value="option" 
              v-model="selectedOption"
              name="survey-option"
              @change="validateForm"
            />
            <label class="mb-0 cursor-pointer" :for="`option-${index}`">{{ option }}</label>
          </div>
          <div v-if="selectedOption === 'Other (please specify)'" class="other-input">
            <textarea 
              v-model="otherText" 
              placeholder="Please specify…" 
              maxlength="180"
              @input="validateForm"
              :class="{ 'error': showOtherTextError }"
            ></textarea>
            <div v-if="showOtherTextError" class="validation-error">
              Please provide details for your response
            </div>
          </div>
          <div v-if="showOptionError" class="validation-error">
            Please select an option
          </div>
        </div>
      </div>
      <div v-if="submitted" class="thank-you-content fs-19">
        <p class="thank-you-message font-weight-thin">Thank you for submitting your <br> response</p>
      </div>
      <div class="exit-survey-footer" v-if="!submitted">
        <button 
          :class="`submit-btn fs-18 ${isActiveBtn ? '' : 'disabled'}`" 
          @click="submitSurvey"
          :disabled="isSubmitting"
        >
          <b-spinner v-if="isSubmitting" small label="Loading..."></b-spinner>
          <span v-else>
            SUBMIT <span class="arrow-right"></span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import surveyService from "@/services/survey";
import {
	getDeviceInfo,
	getUtmString,
} from "@/helper/utils";

export default {
  name: "ExitSurvey",
  data() {
    return {
      isVisible: false,
      submitted: false,
      selectedOption: "",
      otherText: "",
      mouseOutThreshold: 0, // Distance from top in pixels
      excludedPages: ['thank-page', 'top-page'],
      surveyOptions: [
        "I'm just looking around",
        "It's taking too long to submit",
        "I don't understand how AutoFlip works",
        "I'm not selling my car",
        "I had technical issues with the website",
        "Other (please specify)"
      ],
      formSubmitted: false,
      showOptionError: false,
      showOtherTextError: false,
      isSubmitting: false,
    };
  },
  computed: {
    currentPage() {
      return this.$route.name;
    },
    isActiveBtn() {
      if (this.selectedOption && this.selectedOption !== "Other (please specify)") {
        return true;
      }
      return this.selectedOption === "Other (please specify)" && this.otherText.trim().length >= 1;
    }
  },
  mounted() {
    if (!this.hasExitSurveyBeenShown() && !this.excludedPages.includes(this.currentPage)) {
      document.addEventListener('mouseleave', this.handleMouseLeave);
    }
  },
  beforeDestroy() {
    document.removeEventListener('mouseleave', this.handleMouseLeave);
  },
  methods: {
    hasExitSurveyBeenShown() {
      return sessionStorage.getItem('hasShowedExitInstentSurvey') === 'true';
    },
    handleMouseLeave(e) {
      // Only trigger when mouse leaves through the top of the page
      if (e.clientY <= this.mouseOutThreshold && !this.hasExitSurveyBeenShown()) {
        this.showPopup();
      }
    },
    showPopup() {
      this.isVisible = true;
      sessionStorage.setItem('hasShowedExitInstentSurvey', 'true');
      this.formSubmitted = false;
      this.showOptionError = false;
      this.showOtherTextError = false;
      this.submitted = false;
    },
    closePopup() {
      this.isVisible = false;
      sessionStorage.setItem('hasShowedExitInstentSurvey', 'true');
      this.selectedOption = "";
      this.otherText = "";
      this.submitted = false;
      this.formSubmitted = false;
      this.showOptionError = false;
      this.showOtherTextError = false;
    },
    validateForm() {
      // Clear errors when user makes changes
      this.showOptionError = false;
      this.showOtherTextError = false;
    },
    submitSurvey() {
      this.formSubmitted = true;
      const deviceInfo = getDeviceInfo()
      
      // Validate form
      if (!this.selectedOption) {
        this.showOptionError = true;
        return;
      }
      
      if (this.selectedOption === "Other (please specify)" && this.otherText.trim().length === 0) {
        this.showOtherTextError = true;
        return;
      }
      
      const response = {
        response: this.selectedOption === "Other (please specify)" ? this.otherText.trim() : this.selectedOption,
        page: this.currentPage,
        utm: getUtmString(),
        device: deviceInfo.device.type,
				operatingSystem: `${deviceInfo.os.name} ${deviceInfo.os.version}`,
				browser: deviceInfo.client.name,
      };
      
      // Call API to save response
      this.saveResponse(response);
    },
    saveResponse(data) {
      this.isSubmitting = true;
      surveyService.saveExitSurveyResponse(data)
        .then(response => {
          if (response.status === 201 && response.data) {
            this.submitted = true;
          }
        })
        .catch(error => {
          console.error('Error saving survey response:', error);
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.exit-survey {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999999;
  
  &-content {
    background-color: white;
    border-radius: 8px;
    width: 426px;
    height: 545px;
    padding: 24px 35px;
    position: relative;
    transition: height 0.3s ease;
    
    &.expanded {
      height: 645px;
    }

    &.thank-you-mode {
      height: 363px;
    }
  }

  &-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;

    img {
      cursor: pointer;
      width: 20px;
      height: 24px;
    }
  }
  
  &-header {
    color: #1F8252;
    margin-bottom: 16px;
    margin-top: 35px;
    
    p {
      margin: 0;
      font-weight: bold;
    } 
  }

  &-body {
    p {
      color: black; 
    }
  }
  
  &-options {
    margin-top: 16px;
    
    .option {
      margin-bottom: 15px;
      display: flex;
      align-items: center;
      
      input[type="radio"] {
        margin-right: 8px;
        
        &:checked {
          accent-color: #1F8252;
        }
      }

      label {
        color: black;
      }
    }
    
    .other-input {
      margin-top: 8px;
      margin-left: 24px;
      
      textarea {
        width: 330px;
        min-height: 80px;
        padding: 8px;
        border: 1px solid #ced4da;
        border-radius: 4px;
        resize: none;
        outline: none;
        
        &.error {
          border-color: red;
        }

        &::placeholder  {
          font-size: 17px !important;
        }
      }
      
      small {
        display: block;
        text-align: right;
        color: #6c757d;
      }
    }
  }
  
  &-footer {
    margin-top: 24px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    
    .submit-btn {
      background-color: #1F8252;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      width: 355px;
      height: 49px;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      outline: none;
      
      .arrow-right {
        display: inline-block;
        position: relative;
        margin-left: 6px;
        margin-bottom: 2px;
        width: 10px;
        height: 10px;
        
        &::before {
          content: '';
          border-bottom-style: solid;
          border-bottom-width: 2px;
          border-right-style: solid;
          border-right-width: 2px;
          position: absolute;
          top: 50%;
          left: -1px;
          height: 7px;
          width: 7px;
          border-color: white;
          transform: rotate(315deg) translateY(-50%);
        }
      }
      
      &:disabled {
        background-color: #C4C4C4;
        cursor: not-allowed;
      }

      &.disabled {
        background-color: #C4C4C4;
        cursor: not-allowed;
      }
    }
  }
  
  .thank-you-content {
    margin-top: 20px;
    text-align: center;
  }
  
  .thank-you-message {
    font-weight: bold;
    text-align: left;
    margin-top: 60px;
  }
  
  .validation-error {
    color: red;
    font-size: 12px;
    margin-top: 4px;
    font-style: italic;
    text-align: left;
  }
}
</style>